import { ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useInSettingsShell } from '@/components/settings/settingsShellContext';

/**
 * Canonical container for an Organization Settings subpage.
 *
 * Single source of truth for the section chrome — spacing, the title/subtitle
 * block, and the top-right action slot — so every settings section looks
 * identical. Inside a settings layout (`SettingsLayout`) the rail and the
 * breadcrumb already name the section, so the title is dropped and only the
 * description and action remain; there is no frame either. Standalone
 * consumers outside that layout (TenantPage, MemoryBankNotesListingPage) keep
 * the title and re-add the chrome via `className="rounded-xl border shadow-lg"`.
 */
export function SettingsSectionCard({
  title,
  subtitle,
  action,
  children,
  className,
  contentClassName,
  headerClassName,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Button(s) rendered at the top-right of the header. */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  /** Extra classes for the header, e.g. `px-0` to align with the content. */
  headerClassName?: string;
}) {
  const inShell = useInSettingsShell();
  const showHeader = !inShell || Boolean(subtitle) || Boolean(action);
  return (
    <Card
      className={cn('rounded-none border-0 shadow-none gap-2 p-2', className)}
    >
      {showHeader && (
        <CardHeader
          className={cn(
            'flex flex-row items-start justify-between gap-2 p-2',
            headerClassName,
          )}
        >
          <div className="max-w-[50%] space-y-1">
            {!inShell && (
              <CardTitle className="text-xl font-bold">{title}</CardTitle>
            )}
            {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </CardHeader>
      )}
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}
